/* eslint-disable thsjs/misplaced-loop-counter */
/* eslint-disable thsjs/expression-complexity */
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-use-before-define */
/* eslint-disable no-magic-numbers */

import Axis from 'echarts/types/src/coord/Axis';

export type Point = [number, number];

export type Line = { start: Point; end: Point };

export type Rect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export function formatPaddingOrMargin(data: number | number[]) {
  if (typeof data === 'number') {
    return [data, data, data, data];
  }
  if (Array.isArray(data)) {
    const formatData = [...data];
    while (formatData.length < 4) {
      formatData.push(data[formatData.length % data.length]);
    }
    return formatData;
  }
  return [0, 0, 0, 0];
}

// 判断俩个矩形是否相交
export function areRectanglesIntersecting(rectangle1: Rect, rectangle2: Rect): boolean {
  const left1 = rectangle1.x;
  const right1 = rectangle1.x + rectangle1.width;
  const top1 = rectangle1.y;
  const bottom1 = rectangle1.y + rectangle1.height;

  const left2 = rectangle2.x;
  const right2 = rectangle2.x + rectangle2.width;
  const top2 = rectangle2.y;
  const bottom2 = rectangle2.y + rectangle2.height;

  return !(left1 > right2 || right1 < left2 || top1 > bottom2 || bottom1 < top2);
}

// 计算俩个矩形的重叠面积
export function calculateOverlapArea(rect1: Rect, rect2: Rect): number {
  const xOverlap = Math.max(
    0,
    Math.min(rect1.x + rect1.width, rect2.x + rect2.width) - Math.max(rect1.x, rect2.x)
  );
  const yOverlap = Math.max(
    0,
    Math.min(rect1.y + rect1.height, rect2.y + rect2.height) - Math.max(rect1.y, rect2.y)
  );
  return xOverlap * yOverlap;
}

// 俩条线段的交点
export function getLineIntersection(line1: Line, line2: Line): Point[] | null {
  const x1 = line1.start[0];
  const y1 = line1.start[1];
  const x2 = line1.end[0];
  const y2 = line1.end[1];

  const x3 = line2.start[0];
  const y3 = line2.start[1];
  const x4 = line2.end[0];
  const y4 = line2.end[1];

  const denominator = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);

  if (denominator === 0) {
    // 线段平行或重合
    // 如果重合，则将重合的线段的俩个端点看做俩个交点
    return getOverlapSegment(line1, line2);
  }

  const intersectionX =
    ((x1 * y2 - y1 * x2) * (x3 - x4) - (x1 - x2) * (x3 * y4 - y3 * x4)) / denominator;
  const intersectionY =
    ((x1 * y2 - y1 * x2) * (y3 - y4) - (y1 - y2) * (x3 * y4 - y3 * x4)) / denominator;

  // 检查交点是否在两个线段上
  if (
    // eslint-disable-next-line thsjs/expression-complexity
    intersectionX < Math.min(x1, x2) ||
    intersectionX > Math.max(x1, x2) ||
    intersectionX < Math.min(x3, x4) ||
    intersectionX > Math.max(x3, x4) ||
    intersectionY < Math.min(y1, y2) ||
    intersectionY > Math.max(y1, y2) ||
    intersectionY < Math.min(y3, y4) ||
    intersectionY > Math.max(y3, y4)
  ) {
    // 交点不在两个线段上
    return null;
  }

  // 参数归一化
  return [[intersectionX, intersectionY]];
}

// 求给定坐标的正交线段与多边形的交点
export function linePolygonIntersection(coord: number, polygon: Point[]): Point[] {
  // 第零步，确认coord是否在多边形内
  const points = polygon.filter(d => d[0] === coord);
  if (points.length > 0) {
    return points;
  }

  // 第一步，确认相邻的俩个coord坐标
  const polygonSorted = polygon.toSorted((a, b) => a[0] - b[0]) as Point[];
  const leftNearCoordIndex = polygonSorted.findLastIndex(p => p[0] < coord);
  const rightNearCoordIndex = polygonSorted.findLastIndex(p => p[0] > coord);
  // 越界判断
  if (leftNearCoordIndex === -1 || rightNearCoordIndex === -1) {
    return [];
  }

  // 第二步，找到俩个相邻coord的点
  // [index-1, index]
  const leftNearPoints = polygonSorted.slice(leftNearCoordIndex - 1, leftNearCoordIndex + 1);
  const rightNearPoints = polygonSorted.slice(rightNearCoordIndex - 1, rightNearCoordIndex + 1);
  leftNearPoints.sort((a, b) => a[1] - b[1]);
  rightNearPoints.sort((a, b) => a[1] - b[1]);

  // 第三步，找到上方的两个点的连线和下方的俩个点的连线
  const LTPoint = leftNearPoints[0];
  const LBPoint = leftNearPoints[1];
  const RTPoint = rightNearPoints[0];
  const RBPoint = rightNearPoints[1];

  // 第四步，求coord与俩个线段的交点
  const TIntersection = findPointOnSegment(LTPoint, RTPoint, coord);
  const BIntersection = findPointOnSegment(LBPoint, RBPoint, coord);

  return [TIntersection, BIntersection];
}

export function findPointOnSegment(A: Point, B: Point, C: number): Point | null {
  if (C < Math.min(A[0], B[0]) || C > Math.max(A[0], B[0])) {
    return null;
  }

  let y: number;

  if (A[0] === B[0]) {
    // 线段 AB 垂直于 x 轴
    y = A[1] + ((C - A[0]) * (B[1] - A[1])) / (B[0] - A[0]);
  } else {
    const slope = (B[1] - A[1]) / (B[0] - A[0]);
    const intercept = A[1] - slope * A[0];
    y = slope * C + intercept;
  }

  return [C, y];
}

// 求【点集】中X方向和Y方向的最值
export function getMinMax(points: Point[]) {
  const xs = points.map(p => p[0]);
  const ys = points.map(p => p[1]);

  return {
    xMin: Math.min(...xs),
    xMax: Math.max(...xs),
    yMin: Math.min(...ys),
    yMax: Math.max(...ys)
  };
}

// 判断点是否在多边形内
export function isPointInPolygon(point: Point, polygon: Point[]) {
  let cnt = 0;
  for (let i = 0; i < polygon.length; i += 1) {
    const g = point[0];
    const t = point[1];
    const p1g = polygon[i][0];
    const p1t = polygon[i][1];
    const p2g = polygon[i === polygon.length - 1 ? 0 : i + 1][0];
    const p2t = polygon[i === polygon.length - 1 ? 0 : i + 1][1];
    const ming = Math.min(p1g, p2g);
    const maxg = Math.max(p1g, p2g);
    const mint = Math.min(p1t, p2t);
    const maxt = Math.max(p1t, p2t);
    if (
      mint <= t &&
      t <= maxt &&
      ming <= g &&
      (maxg <= g || p2g + (p1g - p2g) * ((t - p2t) / (p1t - p2t)) < g)
    ) {
      cnt += 1;
    }
  }
  return cnt % 2 === 1;
}

// 判断矩形是否在多边形内，非严格判断
// threshold >=2
export function isRectangleInsidePolygon(rectangle: Rect, points: Point[], threshold = 4): boolean {
  const ltPoint: Point = [rectangle.x, rectangle.y];
  const rtPoint: Point = [rectangle.x + rectangle.width, rectangle.y];
  const rbPoint: Point = [rectangle.x + rectangle.width, rectangle.y + rectangle.height];
  const lbPoint: Point = [rectangle.x, rectangle.y + rectangle.height];

  const lcPoint: Point = [rectangle.x + rectangle.width / 4, rectangle.y + rectangle.height / 2];
  const cPoint: Point = [rectangle.x + rectangle.width / 2, rectangle.y + rectangle.height / 2];
  const rcPoint: Point = [
    rectangle.x + (rectangle.width * 3) / 4,
    rectangle.y + rectangle.height / 2
  ];

  // 避免U/N形、W/M形边缘的情况
  const isCenterInside = [lcPoint, cPoint, rcPoint].every(p => {
    return isPointInPolygon(p, points);
  });

  const count = [ltPoint, rtPoint, rbPoint, lbPoint].filter(point => {
    return isPointInPolygon(point, points);
  }).length;

  return isCenterInside && count >= threshold;
}

// 判断俩个线段是否有局部重合
export function areSegmentsOverlapping(segment1: Line, segment2: Line): boolean {
  const { start: p1, end: p2 } = segment1;
  const { start: p3, end: p4 } = segment2;

  const v1 = subtractPoints(p2, p1);
  const v2 = subtractPoints(p3, p1);
  const v3 = subtractPoints(p4, p1);
  const v4 = subtractPoints(p4, p3);
  const v5 = subtractPoints(p2, p3);

  return (
    crossProduct(v1, v2) * crossProduct(v1, v3) <= 0 &&
    crossProduct(v4, v2) * crossProduct(v4, v3) <= 0 &&
    crossProduct(v1, v4) * crossProduct(v1, v5) >= 0
  );
}

export function subtractPoints(p1: Point, p2: Point): Point {
  return [p1[0] - p2[0], p1[1] - p2[1]];
}

function crossProduct(v1: Point, v2: Point): number {
  return v1[0] * v2[1] - v1[1] * v2[0];
}

export function getOverlapSegment(segment1: Line, segment2: Line): Point[] | null {
  if (!areSegmentsOverlapping(segment1, segment2)) {
    return null;
  }

  const { start: p1, end: p2 } = segment1;
  const { start: p3, end: p4 } = segment2;

  const overlapStartX = Math.max(Math.min(p1[0], p2[0]), Math.min(p3[0], p4[0]));
  const overlapStartY = Math.max(Math.min(p1[1], p2[1]), Math.min(p3[1], p4[1]));
  const overlapEndX = Math.min(Math.max(p1[0], p2[0]), Math.max(p3[0], p4[0]));
  const overlapEndY = Math.min(Math.max(p1[1], p2[1]), Math.max(p3[1], p4[1]));

  return [
    [overlapStartX, overlapStartY],
    [overlapEndX, overlapEndY]
  ];
}

// 根据获取当前coord范围的扩展范围，具体规则为前后各扩展50%跨度，如果遇到全区间极值则处理为全区间
export function getExtendCoord(axis: Axis) {
  const { scale } = axis;
  const { rawExtentInfo } = scale;
  // 计算全量数据范围
  const totalDataRange = [rawExtentInfo._dataMin, rawExtentInfo._dataMax];
  // 计算coord全量范围
  const totalCoordRange: [number, number] = [
    axis.toGlobalCoord(axis.dataToCoord(totalDataRange[0])),
    axis.toGlobalCoord(axis.dataToCoord(totalDataRange[1]))
  ];
  // 获取当前数据范围
  const currDataRange = axis.scale.getExtent();
  // 获取当前coord范围
  const currCoordRange: [number, number] = [
    axis.toGlobalCoord(axis.dataToCoord(currDataRange[0])),
    axis.toGlobalCoord(axis.dataToCoord(currDataRange[1]))
  ];
  const currCoordRangeSpan = currCoordRange[1] - currCoordRange[0];

  const extendCoordRange: [number, number] = [
    Math.max(currCoordRange[0] - currCoordRangeSpan / 2, totalCoordRange[0]),
    Math.min(currCoordRange[1] + currCoordRangeSpan / 2, totalCoordRange[1])
  ];

  return extendCoordRange;
}

// 计算线的长度
export function getLineLength(line: Line) {
  const [x1, y1] = line.start;
  const [x2, y2] = line.end;
  return Math.sqrt((x1 - x2) ** 2 + (y1 - y2) ** 2);
}
